import React, { useEffect } from 'react';

// Lightweight Helmet/HelmetProvider shim used to avoid adding react-helmet-async
// (which has peer deps incompatible with React 19 in this project).

export const HelmetProvider = ({ children }) => {
  return <>{children}</>;
};

export const Helmet = ({ children }) => {
  useEffect(() => {
    const nodes = React.Children.toArray(children);

    nodes.forEach((node) => {
      if (!React.isValidElement(node)) return;
      const type = node.type;

      if (type === 'title') {
        const titleText = node.props.children;
        if (typeof titleText === 'string') document.title = titleText;
      }

      if (type === 'meta') {
        const { name, content, property } = node.props;
        const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
        if (name || property) {
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('meta');
            if (property) el.setAttribute('property', property);
            if (name) el.setAttribute('name', name);
            document.head.appendChild(el);
          }
          if (content) el.setAttribute('content', content);
        }
      }

      if (type === 'link') {
        const { rel, href, 'data-helmet': key } = node.props;
        if (rel && href) {
          // Each Helmet link gets its own dedicated element tagged with
          // data-helmet-link, so we never clobber an existing <link> (e.g. the
          // main CSS file) by matching on rel="stylesheet".
          const helmetKey = key || rel;
          const selector = `link[data-helmet-link="${helmetKey}"]`;
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('link');
            el.setAttribute('rel', rel);
            el.setAttribute('data-helmet-link', helmetKey);
            document.head.appendChild(el);
          }
          el.setAttribute('href', href);
        }
      }

      if (type === 'script') {
        // Supports JSON-LD structured data via <script type="application/ld+json" data-helmet="key">
        const { type: scriptType, children: scriptChildren, 'data-helmet': key } = node.props;
        if (scriptType === 'application/ld+json' && scriptChildren) {
          // Use the optional data-helmet key so multiple JSON-LD blocks can coexist
          // and each one can be replaced/cleaned up independently.
          const helmetKey = key || 'default';
          const selector = `script[type="application/ld+json"][data-helmet="${helmetKey}"]`;
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('script');
            el.setAttribute('type', 'application/ld+json');
            el.setAttribute('data-helmet', helmetKey);
            document.head.appendChild(el);
          }
          const payload = typeof scriptChildren === 'string'
            ? scriptChildren
            : JSON.stringify(scriptChildren);
          el.textContent = payload;
        }
      }
    });
  }, [children]);

  return null;
};

export default Helmet;
